import { ChangeDetectorRef, Component, DestroyRef, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { ActivatedRoute, Router } from '@angular/router';
import { PageTitleComponent } from '@howdeniberia/core-front';
import { filter, map, take } from 'rxjs/operators';
import { EditProjectContract } from 'src/app/core/services/api/edit/edit-project.contract';
import { SharedService } from 'src/app/core/services/api/shared/shared.service';
import { toBooleanStrict } from 'src/app/shared/utils';
import { ProjectDraftMapper } from '../mappers/project-draft.mapper';
import { ProjectViewModel } from '../models/project.viewmodel';
import { CalculatorAccidentsComponent } from './calculator-accidents/calculator-accidents.component';
import { CalculatorDeathsComponent } from './calculator-deaths/calculator-deaths.component';
import { CalculatorDependenceComponent } from './calculator-dependence/calculator-dependence.component';
import { CalculatorItComponent } from './calculator-it/calculator-it.component';
import { CalculatorLifeComponent } from './calculator-life/calculator-life.component';

@Component({
  selector: 'howden-sane-project-calculator',
  standalone: true,
  imports: [
    CalculatorLifeComponent,
    CalculatorAccidentsComponent,
    CalculatorItComponent,
    CalculatorDependenceComponent,
    CalculatorDeathsComponent,
    MatCardModule,
    MatButtonModule,
    PageTitleComponent
  ],
  templateUrl: './sane-project-calculator.component.html',
  styleUrls: ['./sane-project-calculator.component.scss']
})
export class SaneProjectCalculatorComponent implements OnInit {
  private readonly route      = inject(ActivatedRoute);
  private readonly router     = inject(Router);
  private readonly sharedSrv  = inject(SharedService);
  private readonly editSrv    = inject(EditProjectContract);
  private readonly cdr        = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb         = inject(FormBuilder);

  public project!: ProjectViewModel;

  private readonly initialized = new Set<string>();

  private _lifeCmp?: CalculatorLifeComponent;
  @ViewChild(CalculatorLifeComponent, { static: false })
  set lifeCmp(cmp: CalculatorLifeComponent | undefined) {
    this._lifeCmp = cmp;
    if (cmp && this.project && this.showLife && !this.initialized.has('life')) {
      cmp.initFromViewModel(this.project.calculatorLife);
      this.initialized.add('life');
      this.cdr.markForCheck();
    }
  }

  private _accidentsCmp?: CalculatorAccidentsComponent;
  @ViewChild(CalculatorAccidentsComponent, { static: false })
  set accidentsCmp(cmp: CalculatorAccidentsComponent | undefined) {
    this._accidentsCmp = cmp;
    if (cmp && this.project && this.showAccidents && !this.initialized.has('accidents')) {
      cmp.initFromViewModel(this.project.calculatorAccidents);
      this.initialized.add('accidents');
      this.cdr.markForCheck();
    }
  }

  private _itCmp?: CalculatorItComponent;
  @ViewChild(CalculatorItComponent, { static: false })
  set itCmp(cmp: CalculatorItComponent | undefined) {
    this._itCmp = cmp;
    if (cmp && this.project && this.showIT && !this.initialized.has('it')) {
      cmp.initFromViewModel(this.project.calculatorIT);
      this.initialized.add('it');
      this.cdr.markForCheck();
    }
  }

  private _deathsCmp?: CalculatorDeathsComponent;
  @ViewChild(CalculatorDeathsComponent, { static: false })
  set deathsCmp(cmp: CalculatorDeathsComponent | undefined) {
    this._deathsCmp = cmp;
    if (cmp && this.project && this.showDeaths && !this.initialized.has('deaths')) {
      cmp.initFromViewModel(this.project.calculatorDeaths);
      this.initialized.add('deaths');
      this.cdr.markForCheck();
    }
  }

  private _dependenceCmp?: CalculatorDependenceComponent;
  @ViewChild(CalculatorDependenceComponent, { static: false })
  set dependenceCmp(cmp: CalculatorDependenceComponent | undefined) {
    this._dependenceCmp = cmp;
    if (cmp && this.project && this.showDependency && !this.initialized.has('dependence')) {
      cmp.initFromViewModel(this.project.calculatorDependence);
      this.initialized.add('dependence');
      this.cdr.markForCheck();
    }
  }

  private readEditValue(ref: string): any {
    if (!this.project) { return null; }
    const name = ref.startsWith('edit.') ? ref.slice(5) : ref;
    const fromEdit = this.project.edit.getControl(name)?.value;
    return fromEdit !== undefined ? fromEdit : this.project.form.get(`edit.${name}`)?.value ?? null;
  }

  private normalizeIndustryFlags(): void {
    if (!this.project?.edit) { return; }
    const e = this.project.edit;
    const coerce = (ctrlName: string) => {
      const c = e.getControl(ctrlName);
      const v = toBooleanStrict(c.value);
      if (c.value !== v) { c.setValue(v, { emitEvent: false }); }
    };
    coerce(e.cIndustryLife);
    coerce(e.cIndustryAccident);
    coerce(e.cIndustryTemporaryIncapacity);
    coerce(e.cIndustryDependency);
    coerce(e.cIndustryDeath);
  }

  private getBool(ref: string): boolean {
    const v = this.readEditValue(ref);
    return toBooleanStrict(v);
  }

  get showLife(): boolean {
    const e = this.project?.edit;
    return !!e && this.getBool(e.cIndustryLife);
  }
  get showAccidents(): boolean {
    const e = this.project?.edit;
    return !!e && this.getBool(e.cIndustryAccident);
  }
  get showIT(): boolean {
    const e = this.project?.edit;
    return !!e && this.getBool(e.cIndustryTemporaryIncapacity);
  }
  get showDependency(): boolean {
    const e = this.project?.edit;
    return !!e && this.getBool(e.cIndustryDependency);
  }
  get showDeaths(): boolean {
    const e = this.project?.edit;
    return !!e && this.getBool(e.cIndustryDeath);
  }

  ngOnInit(): void {
    const cached = this.sharedSrv.getModel();

    if (cached) {
      this.project = cached;
    } else {
      const draft = this.sharedSrv.loadDraft();
      if (!draft) {
        this.router.navigate(['sane-projects', 'edit']);
        return;
      }
      const vm = ProjectDraftMapper.fromDraft(draft, () => new ProjectViewModel(this.fb));
      this.project = vm;
      this.sharedSrv.setModel(vm);
    }

    this.normalizeIndustryFlags();
    this.cdr.detectChanges();

    this.sharedSrv.project$
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe();

    this.route.params
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(p => Number(p['id'] ?? 0)),
        filter(id => !Number.isNaN(id))
      )
      .subscribe(id => {
        if (this.project?.edit) {
          (this.project.edit as any).projectId = id;
        }
      });
  }

  private persistDraft(): void {
    this.sharedSrv.setModel(this.project);
    const draft = ProjectDraftMapper.toDraft(this.project);
    this.sharedSrv.saveDraft(draft);
  }

  onSubmit(): void {
    const editGroup = this.project.form.get('edit');
    const someIndustryOn
      = this.showLife || this.showAccidents || this.showIT || this.showDependency || this.showDeaths;

    if (!editGroup?.valid) { return; }
    if (!someIndustryOn) { return; }

    if (this.showLife)       { this._lifeCmp?.flushToViewModel(); }
    if (this.showAccidents)  { this._accidentsCmp?.flushToViewModel(); }
    if (this.showIT)         { this._itCmp?.flushToViewModel(); }
    if (this.showDeaths)     { this._deathsCmp?.flushToViewModel(); }
    if (this.showDependency) { this._dependenceCmp?.flushToViewModel(); }

    this.persistDraft();
    this.router.navigateByUrl('/sane-projects/resume');
  }

  onCancel(): void {
  if (this.showLife)       { this._lifeCmp?.flushToViewModel(); }
  if (this.showAccidents)  { this._accidentsCmp?.flushToViewModel(); }
  if (this.showIT)         { this._itCmp?.flushToViewModel(); }
  if (this.showDeaths)     { this._deathsCmp?.flushToViewModel(); }
    if (this.showDependency) { this._dependenceCmp?.flushToViewModel(); }

    this.persistDraft();

    const id = (this.project.edit as any).projectId || 0;

    if (id > 0) {
      this.router.navigate(['sane-projects', 'edit', id], { state: { fromCalculator: true } });
    } else {
      this.router.navigate(['sane-projects', 'edit'], { state: { fromCalculator: true } });
    }
  }
}
